// Everything is stored on this device only, in IndexedDB. Nothing is sent to a server.
// The address book and ledger are encrypted with a key that comes from your
// recovery phrase, so they can only be read once Relay is unlocked.
const DB = 'drop-wallet', VER = 1;
const MAX_LEDGER = 1000;
let dbp;

function db() {
  if (!dbp) dbp = new Promise((res, rej) => {
    const r = indexedDB.open(DB, VER);
    r.onupgradeneeded = () => {
      const d = r.result;
      d.createObjectStore('kv');
      d.createObjectStore('contacts', { keyPath: 'address' }); // old, plain-text (migrated then emptied)
      d.createObjectStore('ledger', { keyPath: 'id' });         // old, plain-text (migrated then emptied)
    };
    r.onsuccess = () => { const d = r.result; d.onversionchange = () => { d.close(); dbp = null; }; res(d); };
    r.onerror = () => rej(r.error);
  });
  return dbp;
}

async function tx(store, mode, fn) {
  const d = await db();
  return new Promise((res, rej) => {
    const t = d.transaction(store, mode);
    const s = t.objectStore(store);
    let out;
    Promise.resolve(fn(s)).then((v) => { out = v; }, () => {});
    t.oncomplete = () => res(out);
    t.onerror = () => rej(t.error);
    t.onabort = () => rej(t.error || new Error('Storage is full or unavailable.'));
  });
}
const req = (r) => new Promise((res, rej) => { r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); });

export const kv = {
  get: (k) => tx('kv', 'readonly', (s) => req(s.get(k))),
  set: (k, v) => tx('kv', 'readwrite', (s) => req(s.put(v, k))),
  del: (k) => tx('kv', 'readwrite', (s) => req(s.delete(k))),
};

// ---------------------------------------------------------------- encrypted data
let key = null;
const b64 = (u) => btoa(String.fromCharCode(...new Uint8Array(u)));
const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

export async function setDataKey(raw) {
  key = raw ? await crypto.subtle.importKey('raw', raw, 'AES-GCM', false, ['encrypt', 'decrypt']) : null;
}
export function clearDataKey() { key = null; }

async function getSecret(name, fallback) {
  const box = await kv.get('enc:' + name);
  if (!box || !key) return fallback;
  try {
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64(box.iv), additionalData: new TextEncoder().encode(name) }, key, unb64(box.ct));
    return JSON.parse(new TextDecoder().decode(pt));
  } catch { return fallback; } // made by a different wallet: start fresh
}
async function setSecret(name, value) {
  if (!key) throw new Error('Relay is locked.');
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: new TextEncoder().encode(name) }, key, new TextEncoder().encode(JSON.stringify(value)));
  await kv.set('enc:' + name, { iv: b64(iv), ct: b64(ct) });
}

// One write at a time, so two quick updates can't overwrite each other.
let q = Promise.resolve();
const serial = (fn) => (q = q.then(fn, fn));

export const contacts = {
  all: () => getSecret('contacts', []),
  put: (c) => serial(async () => { const l = (await contacts.all()).filter((x) => x.address !== c.address); l.push(c); await setSecret('contacts', l); }),
  del: (a) => serial(async () => setSecret('contacts', (await contacts.all()).filter((x) => x.address !== a))),
};
export const ledger = {
  all: async () => (await getSecret('ledger', [])).sort((a, b) => b.at - a.at),
  put: (r) => serial(async () => {
    const l = (await getSecret('ledger', [])).filter((x) => x.id !== r.id);
    l.push(r); l.sort((a, b) => b.at - a.at);
    await setSecret('ledger', l.slice(0, MAX_LEDGER));
  }),
};

export const blocked = {
  all: () => getSecret('blocked', []),
  add: (address) => serial(async () => { const l = (await blocked.all()).filter((a) => a !== address); l.push(address); await setSecret('blocked', l.slice(-500)); }),
  del: (address) => serial(async () => setSecret('blocked', (await blocked.all()).filter((a) => a !== address))),
};

// ---------------------------------------------------------------- waiting to send
// Files kept on this device while the other person is offline (doorbell). Each file is
// stored in 4 MB pieces, every piece encrypted with the wallet's key, and deleted once
// sent, cancelled, or after 48 hours. Nothing here ever goes to a server.
const QPIECE = 4 * 1024 * 1024;   // a multiple of the 64 KB transfer chunk, so reads never straddle pieces
export const QUEUE_LIFE = 48 * 60 * 60 * 1000;
const te = (s) => new TextEncoder().encode(s);
async function sealPiece(name, buf) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  return { iv: b64(iv), ct: await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: te(name) }, key, buf) };
}
async function openPiece(name, box) {
  return crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64(box.iv), additionalData: te(name) }, key, box.ct);
}
const delPrefix = (prefix) => tx('kv', 'readwrite', (s) => req(s.delete(IDBKeyRange.bound(prefix, prefix + '￿'))));

// A stand-in for a File that reads (and decrypts) one stored piece at a time.
class QueuedFile {
  constructor(id, fi, meta) { Object.assign(this, { id, fi, name: meta.name, size: meta.size, type: meta.type || 'application/octet-stream' }); this.cache = null; }
  slice(start, end) {
    const self = this;
    return { async arrayBuffer() {
      const pi = Math.floor(start / QPIECE);
      if (!self.cache || self.cache.pi !== pi) {
        const name = `qf:${self.id}:${self.fi}:${pi}`;
        const box = await kv.get(name);
        if (!box) throw new Error('A waiting file is missing from this device.');
        self.cache = { pi, buf: await openPiece(name, box) };
      }
      const off = pi * QPIECE;
      return self.cache.buf.slice(start - off, Math.min(end, self.size) - off);
    } };
  }
}

export const queue = {
  all: () => getSecret('queue', []),
  // Saves the files encrypted, then the list entry. Calls progress(bytesDone) as it goes.
  add: async (item, files, progress = () => {}) => {
    if (!key) throw new Error('Relay is locked.');
    let done = 0;
    try {
      for (let fi = 0; fi < files.length; fi++) {
        const f = files[fi];
        for (let pi = 0; pi * QPIECE < Math.max(f.size, 1); pi++) {
          const buf = await f.slice(pi * QPIECE, (pi + 1) * QPIECE).arrayBuffer();
          const name = `qf:${item.id}:${fi}:${pi}`;
          await kv.set(name, await sealPiece(name, buf));
          done += buf.byteLength; progress(done);
        }
      }
    } catch (e) { await delPrefix(`qf:${item.id}:`).catch(() => {}); throw e; }
    await serial(async () => setSecret('queue', [...(await getSecret('queue', [])).filter((x) => x.id !== item.id), item]));
  },
  files: (item) => item.files.map((m, fi) => new QueuedFile(item.id, fi, m)),
  del: (id) => serial(async () => { await setSecret('queue', (await getSecret('queue', [])).filter((x) => x.id !== id)); await delPrefix(`qf:${id}:`); }),
  // Removes anything past its 48 hours, and pieces left behind by an interrupted save.
  prune: () => serial(async () => {
    const list = await getSecret('queue', []), now = Date.now();
    const keep = list.filter((x) => now - x.at < QUEUE_LIFE);
    if (keep.length !== list.length) await setSecret('queue', keep);
    const ids = new Set(keep.map((x) => x.id));
    const keys = await tx('kv', 'readonly', (s) => req(s.getAllKeys(IDBKeyRange.bound('qf:', 'qf:￿'))));
    for (const id of new Set(keys.map((k) => String(k).split(':')[1]))) if (!ids.has(id)) await delPrefix(`qf:${id}:`);
    return keep;
  }),
};

// Moves anything saved in plain text by older test builds into encrypted storage.
export async function migratePlain() {
  const oldC = await tx('contacts', 'readonly', (s) => req(s.getAll()));
  const oldL = await tx('ledger', 'readonly', (s) => req(s.getAll()));
  if (!oldC.length && !oldL.length) return;
  for (const c of oldC) await contacts.put(c);
  for (const r of oldL) await ledger.put(r);
  await tx('contacts', 'readwrite', (s) => req(s.clear()));
  await tx('ledger', 'readwrite', (s) => req(s.clear()));
}

export async function wipe() {
  const d = await db(); d.close(); dbp = null; key = null;
  await new Promise((res, rej) => {
    const r = indexedDB.deleteDatabase(DB);
    r.onsuccess = () => res();
    r.onerror = () => rej(r.error);
    r.onblocked = () => rej(new Error('Close Relay in your other tabs, then try again.'));
  });
}
